"use client";

import { Ellipsis, MessageSquare, MessageSquareOff, Trash2, UserX } from "lucide-react";
import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { Popover } from "@/components/ui/atoms/popover";

/**
 * Les gestes de l'interprète sur une personne de la salle : retirer ou rendre
 * la parole, exclure — et, sur une ligne du fil, retirer le message.
 *
 * # Un seul menu, deux endroits
 *
 * Le même bouton sur une ligne du fil et sur une ligne de la salle : la
 * modération se fait là où l'on lit ce qui gêne, et là où l'on voit qui est
 * là. Un geste par entrée, jamais de sous-menu : trois lignes au plus, et
 * l'exclusion en dernier, derrière un filet.
 *
 * # L'exclusion se confirme, dans le menu
 *
 * Pas de boîte de dialogue : elle couvrirait le rouleau pendant que
 * l'interprète joue. L'entrée se transforme — « Exclure du récital » devient
 * « Confirmer l'exclusion », en rouge — et un second clic exclut ; fermer le
 * menu remet l'entrée à sa place. Un clic pour muter, deux pour exclure :
 * proportionné à ce que chacun défait.
 *
 * # Rien n'est appliqué localement
 *
 * Le geste part au service, et ce qu'il a fait revient par la salle — la
 * ligne disparaît, la marque « sourdine » s'affiche — comme un message. Le
 * menu ne dit que s'il n'est pas parti.
 */

export interface ModerationTarget {
  readonly identity: string;
  readonly name: string;
  /** Tel que l'interprète le sait : le relevé, ou son propre geste. */
  readonly muted: boolean;
}

export interface ModerationGestures {
  readonly mute: (identity: string, muted: boolean) => Promise<boolean>;
  readonly exclude: (identity: string) => Promise<boolean>;
}

export function ModerationMenu({
  target,
  gestures,
  onRetract,
  reveal = "always",
}: {
  target: ModerationTarget;
  gestures: ModerationGestures;
  /** Retire le message de cette ligne : sur le fil seulement. */
  onRetract?: () => Promise<boolean>;
  /**
   * `hover` : le bouton ne se montre qu'au survol de la ligne (`group`), au
   * clavier, et toujours au doigt — le fil en porterait sinon un par ligne.
   */
  reveal?: "always" | "hover";
}) {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const menuId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setConfirming(false);
    setFailed(false);
  }, []);

  const perform = async (gesture: () => Promise<boolean>) => {
    setBusy(true);
    setFailed(false);

    const ok = await gesture();

    setBusy(false);

    if (ok) {
      close();

      return;
    }

    setFailed(true);
    setConfirming(false);
  };

  const revealClass =
    reveal === "hover"
      ? "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 [@media(pointer:coarse)]:opacity-100"
      : "";

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Modérer ${target.name}`}
        title="Modérer"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-text-muted transition-opacity hover:bg-white/[0.06] hover:text-text-primary focus:outline-none focus-visible:ring-2 ${revealClass}`}
        style={{ ["--tw-ring-color" as string]: "var(--color-accent)" }}
      >
        <Ellipsis size={14} strokeWidth={1.8} aria-hidden />
      </button>

      <Popover anchorRef={anchor} open={open} onClose={close} placement="bottom-end" offset={2} className="p-1">
        <div id={menuId} role="menu" aria-label={`Modérer ${target.name}`} className="w-[220px]">
          <p className="m-0 truncate px-2.5 pb-1 pt-1.5 text-[11px] text-text-muted">{target.name}</p>

          {onRetract !== undefined && (
            <Item
              icon={<Trash2 size={14} strokeWidth={1.8} aria-hidden />}
              label="Retirer le message"
              disabled={busy}
              onSelect={() => void perform(onRetract)}
            />
          )}

          <Item
            icon={
              target.muted ? (
                <MessageSquare size={14} strokeWidth={1.8} aria-hidden />
              ) : (
                <MessageSquareOff size={14} strokeWidth={1.8} aria-hidden />
              )
            }
            label={target.muted ? "Rendre la parole" : "Mettre en sourdine"}
            disabled={busy}
            onSelect={() => void perform(() => gestures.mute(target.identity, !target.muted))}
          />

          <div className="mx-1.5 my-1 h-px" style={{ background: "var(--color-border-subtle)" }} />

          <Item
            icon={<UserX size={14} strokeWidth={1.8} aria-hidden />}
            label={confirming ? "Confirmer l’exclusion" : "Exclure du récital"}
            tone={confirming ? "danger" : "normal"}
            disabled={busy}
            onSelect={() => {
              if (!confirming) {
                setConfirming(true);

                return;
              }

              void perform(() => gestures.exclude(target.identity));
            }}
          />

          {failed && (
            <p role="alert" className="m-0 px-2.5 pb-1.5 pt-1 text-[11px] leading-[1.4] text-red-300">
              Le geste n’est pas passé. Réessaie.
            </p>
          )}
        </div>
      </Popover>
    </>
  );
}

function Item({
  icon,
  label,
  onSelect,
  disabled,
  tone = "normal",
}: {
  icon: ReactNode;
  label: string;
  onSelect: () => void;
  disabled: boolean;
  tone?: "normal" | "danger";
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSelect();
      }}
      className="flex h-[34px] w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] transition-colors duration-150 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent disabled:cursor-default disabled:opacity-40"
      style={{ color: tone === "danger" ? "#fca5a5" : "var(--color-text-primary)" }}
    >
      <span className="shrink-0" style={{ color: tone === "danger" ? "#fca5a5" : "var(--color-text-muted)" }}>
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

/** « sourdine », à côté d'un nom : ce que l'interprète seul voit. */
export function MutedTag() {
  return (
    <span
      className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] uppercase leading-[1.4]"
      style={{ letterSpacing: "0.12em", color: "#fcd34d", background: "rgba(251, 191, 36, 0.14)" }}
    >
      sourdine
    </span>
  );
}
